/*
 * Design tokens. Every color is a var() reference into
 * styles/theme/tokens.css, which defines the light values on :root and the
 * dark overrides on [data-theme="dark"] — so anything styled through
 * `theme.*` is automatically theme-aware. Add new colors in BOTH files.
 *
 * These values are CSS strings: fine anywhere Emotion/inline styles/SVG
 * props go, but NOT resolvable in plain JS (canvas drawing, color math).
 * For those, read the computed style off an element instead.
 */
export const theme = {
  colors: {
    background: "var(--color-background)",
    foreground: "var(--color-foreground)",
    card: "var(--color-card)",
    cardForeground: "var(--color-card-foreground)",
    popover: "var(--color-popover)",
    popoverForeground: "var(--color-popover-foreground)",
    primary: "var(--color-primary)",
    primaryForeground: "var(--color-primary-foreground)",
    secondary: "var(--color-secondary)",
    secondaryForeground: "var(--color-secondary-foreground)",
    muted: "var(--color-muted)",
    mutedForeground: "var(--color-muted-foreground)",
    accent: "var(--color-accent)",
    accentForeground: "var(--color-accent-foreground)",
    destructive: "var(--color-destructive)",
    destructiveForeground: "var(--color-destructive-foreground)",
    border: "var(--color-border)",
    input: "var(--color-input)",
    ring: "var(--color-ring)",
    chart1: "var(--chart-1)",
    chart2: "var(--chart-2)",
    chart3: "var(--chart-3)",
    chart4: "var(--chart-4)",
    chart5: "var(--chart-5)",
    sidebar: "var(--color-sidebar)",
    sidebarForeground: "var(--color-sidebar-foreground)",
    sidebarPrimary: "var(--color-sidebar-primary)",
    sidebarPrimaryForeground: "var(--color-sidebar-primary-foreground)",
    sidebarAccent: "var(--color-sidebar-accent)",
    sidebarAccentForeground: "var(--color-sidebar-accent-foreground)",
    sidebarBorder: "var(--color-sidebar-border)",
    sidebarRing: "var(--color-sidebar-ring)",
    nucleus: "var(--color-nucleus)",
    // Gray ramp: 50 is always closest to the page background, 900 closest to
    // the text color (the ramp flips in dark mode)
    gray50: "var(--color-gray-50)",
    gray100: "var(--color-gray-100)",
    gray200: "var(--color-gray-200)",
    gray300: "var(--color-gray-300)",
    gray400: "var(--color-gray-400)",
    gray500: "var(--color-gray-500)",
    gray600: "var(--color-gray-600)",
    gray700: "var(--color-gray-700)",
    gray800: "var(--color-gray-800)",
    gray900: "var(--color-gray-900)",
    midGrey: "var(--color-mid-grey)", // Mid grey for action buttons
    secondaryDark: "var(--color-secondary-dark)", // Slate-tinted dark grey for secondary action buttons (paler than `primary`)
    secondaryDarkHover: "var(--color-secondary-dark-hover)", // Hover for `secondaryDark` (one step darker)
    blue50: "var(--color-blue-50)",
    blue100: "var(--color-blue-100)",
    blue500: "var(--color-blue-500)",
    blue600: "var(--color-blue-600)",
    blue700: "var(--color-blue-700)",
    blue800: "var(--color-blue-800)",
    red50: "var(--color-red-50)", // Light red background
    red200: "var(--color-red-200)", // Darker salmon for zero/stockout
    red600: "var(--color-red-600)",
    red700: "var(--color-red-700)", // Dark red text
    orange50: "var(--color-orange-50)",
    orange100: "var(--color-orange-100)",
    orange200: "var(--color-orange-200)",
    orange500: "var(--color-orange-500)",
    orange600: "var(--color-orange-600)",
    yellow100: "var(--color-yellow-100)", // Light yellow background
    yellow600: "var(--color-yellow-600)", // Yellow/orange for warnings
    green50: "var(--color-green-50)",
    green100: "var(--color-green-100)", // Darker green background
    green200: "var(--color-green-200)", // Lighter green background
    green500: "var(--color-green-500)",
    green600: "var(--color-green-600)", // Medium green text
    green700: "var(--color-green-700)", // Dark green text
    clearBtnBg: "var(--color-clear-btn-bg)",
    white: "var(--color-white)",
    pastPeriodBg: "var(--color-past-period-bg)", // Same as muted — consistent grey for past/current period columns
    // Status colors - centralized for consistent use across the app
    status: {
      error: "var(--color-status-error)", // Red for errors/negative values
      warning: "var(--color-status-warning)", // Yellow/orange for warnings
      success: "var(--color-status-success)", // Green for success/healthy
    },
    // Item status colors for purchase orders and items
    itemStatus: {
      sent: "var(--color-item-status-sent)", // Green for sent items
      skipped: "var(--color-item-status-skipped)", // Grey for skipped items
      needsReview: "var(--color-item-status-needs-review)", // Yellow for items needing review
    },
    // Filter action colors
    filter: {
      applied: "var(--color-filter-applied)", // Green for applied filters
      appliedBg: "var(--color-filter-applied-bg)", // Dark green background (applied)
      pendingBg: "var(--color-filter-pending-bg)", // Light green background (pending)
      pendingIcon: "var(--color-filter-pending-icon)", // Faded green icon (pending)
      delete: "var(--color-filter-delete)", // Red for delete/remove
    },
  },
  borderRadius: {
    sm: "calc(0.625rem - 4px)",
    md: "calc(0.625rem - 2px)",
    lg: "0.625rem",
    xl: "calc(0.625rem + 4px)",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    "2xl": "3rem",
  },
  layout: {
    sidebarWidth: "5rem",
    headerHeight: "auto",
  },
  shadows: {
    xs: "var(--shadow-xs)",
    sm: "var(--shadow-sm)",
    md: "var(--shadow-md)",
    lg: "var(--shadow-lg)",
  },
  typography: {
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
};

export type Theme = typeof theme;

/**
 * Apply alpha to any theme color (including var() references).
 * e.g. alpha(theme.colors.muted, 0.5)
 *   → "color-mix(in srgb, var(--color-muted) 50%, transparent)"
 */
export const alpha = (color: string, opacity: number): string =>
  `color-mix(in srgb, ${color} ${opacity * 100}%, transparent)`;
